import type { AiCostLimitLevel } from '@Domain/AiCostLimits/AiCostLimitLevel';
import type { AiCostLimitState } from '@Domain/AiCostLimits/AiCostLimitState';
import type { SubjectNameResolver } from '@Guidelines/Shell/AiCostLimitBanner/Hooks/useSubjectNameResolver';
import type AiCostLimitAlertView from '@UI/Store/AiCostLimitAlerts/Views/AiCostLimitAlertView';

type AlertedState = Exclude<AiCostLimitState, 'below'>;
type SentenceWriter = (percent: number, name: string) => string;

// Whole sentences: French articles and agreements forbid composing them from words.
const SENTENCES: Readonly<Record<AiCostLimitLevel, Readonly<Record<AlertedState, SentenceWriter>>>> = {
	account: {
		alert: (percent) => `${percent} % du plafond du compte atteint.`,
		reached: () => "Plafond du compte atteint : aucun nouveau traitement d'IA ne peut démarrer.",
		exceeded: () => "Plafond du compte dépassé : aucun nouveau traitement d'IA ne peut démarrer.",
	},
	organization: {
		alert: (percent, name) => `${percent} % du plafond de la structure ${name} atteint.`,
		reached: (_percent, name) =>
			`Plafond de la structure ${name} atteint : aucun nouveau traitement d'IA ne peut y démarrer.`,
		exceeded: (_percent, name) =>
			`Plafond de la structure ${name} dépassé : aucun nouveau traitement d'IA ne peut y démarrer.`,
	},
	facility: {
		alert: (percent, name) => `${percent} % du plafond de l'agence ${name} atteint.`,
		reached: (_percent, name) =>
			`Plafond de l'agence ${name} atteint : aucun nouveau traitement d'IA ne peut y démarrer.`,
		exceeded: (_percent, name) =>
			`Plafond de l'agence ${name} dépassé : aucun nouveau traitement d'IA ne peut y démarrer.`,
	},
	user: {
		alert: (percent) => `${percent} % de votre plafond atteint.`,
		reached: () =>
			"Votre plafond est atteint : aucun nouveau traitement d'IA ne peut démarrer pour vous.",
		exceeded: () =>
			"Votre plafond est dépassé : aucun nouveau traitement d'IA ne peut démarrer pour vous.",
	},
};

type AiCostLimitAlert = Pick<AiCostLimitAlertView, 'level' | 'subjectId' | 'state' | 'percent'>;

function resolveSubjectName(alert: AiCostLimitAlert, findName: SubjectNameResolver): string | null {
	if (alert.level === 'organization' || alert.level === 'facility') {
		return findName(alert.level, alert.subjectId);
	}

	return '';
}

/**
 * The sentence for one alert, or `null` when it must not be shown: a state below the threshold, or a
 * structure or agency whose name `findName` cannot resolve.
 */
export default function resolveAlertSentence(
	alert: AiCostLimitAlert,
	findName: SubjectNameResolver,
): string | null {
	if (alert.state === 'below') {
		return null;
	}

	const name = resolveSubjectName(alert, findName);

	if (name === null) {
		return null;
	}

	return SENTENCES[alert.level][alert.state](alert.percent, name);
}
